<script lang="ts" setup>
import { computed } from "vue";
import type { PropType } from "vue";
import { accountKisses } from "/api";
import { useUserListStore } from "/stores/user_list_store";
import { i18n as I18n } from "/locales/i18n";
import { disabledButtons } from "/libs/kisses";
import UserType from "./user_type.vue";
import type * as UserList from "./types/user_list";

defineOptions({
  name: "UserCard",
});

const props = defineProps({
  user: { type: Object as PropType<UserList.User>, required: true },
});

const locale = I18n.locale;
const tCertified = I18n.t("user.certified");

const compactAge = computed(() => {
  if (props.user.userTypeId === 1) {
    if (props.user.fAge == null || props.user.mAge == null) return null;
    return `${props.user.fAge}&${props.user.mAge}`;
  }

  if (props.user.userTypeId === 2) return props.user.fAge ?? null;
  return props.user.mAge ?? null;
});

// Set view-transition-name only at click time, so the cross-doc VT matches on the way out
// (index→show) but not on back (show→index, where Vue cards would be missing/mispositioned
// anyway and cause a flash).
function prepareViewTransition(event: MouseEvent) {
  if (!(event.currentTarget instanceof Element)) return;

  const card = event.currentTarget.closest("user-card");
  if (card instanceof HTMLElement)
    card.style.viewTransitionName = `user-${props.user.id}-avatar`;
}

const approxOnlineStatus = computed(() => {
  const onlineAt = props.user.onlineAt;
  const ageMs = onlineAt ? Date.now() - new Date(onlineAt).getTime() : Infinity;
  if (props.user.isOnline || ageMs < 30 * 60 * 1000)
    return I18n.t("user.online_status.now");
  if (ageMs < 24 * 60 * 60 * 1000) return I18n.t("user.online_status.today");
  if (ageMs < 7 * 24 * 60 * 60 * 1000)
    return I18n.t("user.online_status.recently");
  return null;
});

const approxDistance = computed(() => {
  const distance = props.user.distance;
  if (distance == null) return null;
  if (distance <= 3) return I18n.t("user.geolocation.just_next_to");
  if (distance <= 20) return I18n.t("user.geolocation.very_close");
  if (distance <= 50) return I18n.t("user.geolocation.nearby");
  return null;
});

function seek(userTypeIds: number[], format: "short" | "plural" = "short") {
  const types = I18n.t<(string | null)[]>(`common.user_types.${format}`);
  return userTypeIds
    .map((id) => types[id] ?? "")
    .join(", ")
    .toLowerCase();
}

const openToMessage = computed(() => {
  const message = I18n.t(`user.open_to.${props.user.userTypeId}`, {
    user_types: seek(props.user.seekUserTypes ?? [], "plural"),
    defaultValue: null,
  });
  return typeof message === "string" ? message : null;
});

const commonFantasies = computed(() => {
  const indexes = props.user.commonFantasyIndexes ?? [];
  const labels = I18n.t<string[]>("user_detail.fantasy_preferences");
  return indexes.map((i) => {
    const l = (labels[i] ?? "").toLowerCase();
    return l.charAt(0).toUpperCase() + l.slice(1);
  });
});

const hotNoteVisible = computed(
  () => useUserListStore().hotNoteEnabled && !props.user.isCurrent,
);

// sentKiss lives on the user object (owned by the parent's store, set from the kiss:sent stream event)
// so the state survives the virtual scroller recycling this instance and Turbo back-nav restoring the
// cached list.
const disabled = computed(() => disabledButtons(props.user.sentKiss));
const kissDisabled = computed(
  () => props.user.isCurrent || disabled.value.includes("kiss"),
);
const premiumKissDisabled = computed(
  () => props.user.isCurrent || disabled.value.includes("french_kiss"),
);
</script>

<template>
  <!-- @vue-ignore Custom element, not a recursive UserCard instance. -->
  <div
    :class="{ 'is-crush': user.hasMatched, 'has-french-kiss': user.hasFrenchKiss }"
    :id="`si-${user.id}`"
    class="user-card has-kiss-tag-animation"
    data-controller="history"
  >
    <a
      :data-anchor="`si-${user.id}`"
      :href="user.path"
      class="user-card__link ios:disable-touch-context"
      data-action="history#addAnchorToCurrentState"
      @click="prepareViewTransition"
    >

      <img
        v-if="user.avatarUrl"
        :src="user.avatarUrl"
        alt=""
        class="user-card__img ios:disable-touch-context"
        height="546"
        width="410"
      />
      <div v-else class="user-card__img-placeholder bg-disabled flex justify-center h-full w-full">
        <svg
          class="fill-text-disabled"
          fill="none"
          height="101"
          viewBox="0 0 171 101"
          width="171"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M112.685 1.24163C109.986 -0.567098 106.419 -0.38456 103.919 1.69021L89.9789 13.259C87.2624 15.5134 83.3251 15.5125 80.6097 13.2567L66.724 1.7214C64.2255 -0.354184 60.6593 -0.538875 57.9597 1.2675L2.65152 38.2765C-0.23913 40.2108 -0.871994 44.1962 1.27727 46.9308C28.781 82.0828 56.9929 100.139 85.2934 100.859C113.594 100.139 141.806 82.0528 169.309 46.9308C171.459 44.1955 170.828 40.2094 167.938 38.2726L112.685 1.24163Z"
          />
        </svg>
      </div>

      <span class="user-badges">
        <span
          v-if="user.hasMatched"
          :class="`locale-${locale}`"
          class="user-badge user-badge--crushes"
        >
          <span class="user-badge__icon">
            <svg viewBox="0 0 49 48" xmlns="http://www.w3.org/2000/svg">
              <path
                clip-rule="evenodd"
                d="M14.04 7.64a9.2 9.2 0 0 1 12.3 4.43l.84 1.81c.23.5.82.7 1.32.48l1.82-.86a9.2 9.2 0 0 1 12.3 4.43 9.3 9.3 0 0 1-4.52 12.3l-17.57 8.22c-1.1.52-2.4.05-2.9-1.04l-8.1-17.48a9.3 9.3 0 0 1 4.51-12.29"
                fill="currentColor"
                fill-rule="evenodd"
              />
            </svg>
          </span>
          <svg class="user-badge__svg-text" viewBox="0 0 80 80">
            <defs>
              <path
                :id="`crushPath-${user.id}`"
                d="M 40, 40 m -28, 0 a 28,28 0 1,1 56,0 a 28,28 0 1,1 -56,0"
              />
            </defs>
            <text class="user-badge__text" text-anchor="middle">
              <textPath :href="`#crushPath-${user.id}`" startOffset="50%">
                {{ I18n.t("friend.you_are_crushes") }}
              </textPath>
            </text>
          </svg>
        </span>

        <span
          v-if="user.hasFrenchKiss"
          :class="`locale-${locale}`"
          class="user-badge user-badge--french-kiss"
        >
          <span class="user-badge__icon">
            <svg fill="none" viewBox="0 0 49 48" xmlns="http://www.w3.org/2000/svg">
              <path d="M31.2737 15.1751C32.4288 15.8336 33.4624 16.9033 34.0725 18.154C34.5745 19.2142 34.8859 20.8729 35.2203 22.6308C35.8276 25.8955 36.549 29.7422 38.8336 32.1294C39.0962 32.4038 39.0163 32.877 38.6635 33.0238C35.3923 34.3848 31.4207 36.5288 28.7783 37.9598C27.9643 38.3945 27.2849 38.7866 26.7794 39.0291C24.8364 40.0276 22.5358 40.1392 20.9081 39.3105C19.5332 38.6031 18.6867 37.2673 18.4442 35.4212C18.4541 35.3944 18.4377 35.3581 18.4311 35.2951C19.9309 35.5016 22.0051 35.2782 24.1419 33.3488C25.3991 32.1943 26.1542 30.7074 26.3383 29.0755C27.1358 29.0898 27.9399 29.1671 28.7505 29.3073C29.2263 29.3877 29.7154 29.109 29.8828 28.6537C30.0503 28.1985 29.8305 27.6645 29.4138 27.4235C27.1856 26.2876 25.2072 23.4221 24.6728 20.5915C24.6663 20.5285 24.6433 20.4292 24.6466 20.3394C24.3876 19.1848 24.6438 18.246 24.6438 18.246C24.7785 17.4758 25.0641 16.6992 25.6484 15.9999C26.6496 14.8139 27.9461 14.2801 29.3671 14.4582C29.3671 14.4582 29.4196 14.4771 29.4459 14.4866C29.4459 14.4866 30.4534 14.5764 31.3098 15.1578L31.2737 15.1751ZM16.8104 32.5888C15.675 31.8767 14.7071 30.7094 14.1364 29.3516C13.933 28.8538 13.641 28.1119 13.3261 27.2707C12.2316 24.494 10.5905 20.3028 8.97007 17.1764C8.79543 16.8394 9.04175 16.4261 9.42117 16.3829C12.7223 16.0071 15.7741 13.5192 18.3604 11.4044C19.7586 10.2704 21.0847 9.17109 22.1481 8.70494C24.1404 7.81516 26.5493 7.89415 28.1605 8.9292C29.3648 9.69643 29.9848 10.9204 30.0074 12.4751C29.9976 12.5019 29.9779 12.5554 29.9943 12.5917C29.8893 12.5539 29.7843 12.516 29.6596 12.5318C27.5855 12.2701 25.649 13.089 24.1948 14.779C23.2593 15.8674 22.7372 17.2866 22.6351 18.857C21.5915 19.027 20.5544 19.0175 19.6749 18.8221C19.1827 18.7054 18.6936 18.9842 18.5163 19.4662C18.339 19.9482 18.5325 20.4727 18.9591 20.687C21.8632 22.2483 23.8548 24.7546 24.3204 27.2875C24.3204 27.2875 24.517 28.45 24.3528 29.3006C24.1885 30.1512 23.7814 31.0962 22.8557 31.9152C21.5263 33.1044 20.0986 33.591 18.6448 33.3404C18.6448 33.3404 17.3715 33.0032 16.8006 32.6156L16.8104 32.5888Z" fill="currentColor"/>
            </svg>
          </span>
          <svg class="user-badge__svg-text" viewBox="0 0 80 80">
            <defs>
              <path
                :id="`circlePath-${user.id}`"
                d="M 40, 40 m -28, 0 a 28,28 0 1,1 56,0 a 28,28 0 1,1 -56,0"
              />
            </defs>
            <text class="user-badge__text" text-anchor="middle">
              <textPath
                :href="`#circlePath-${user.id}`"
                class="received"
                startOffset="50%"
              >
                {{ I18n.t("french_kiss.received.long") }}
              </textPath>
              <textPath :href="`#circlePath-${user.id}`" class="sent" startOffset="50%">
                {{ I18n.t("french_kiss.sent.short") }}
              </textPath>
            </text>
          </svg>
        </span>
      </span>
    </a>

    <div class="user-card__footer">
      <div class="user-card__footer-content">
        <a
          :data-anchor="`si-${user.id}`"
          :href="user.path"
          class="block no-underline"
          data-action="history#addAnchorToCurrentState"
          @click="prepareViewTransition"
        >
          <div class="grid gap-1">
            <span v-if="user.hasHotNote" class="flex gap-1">
              <span class="tag is-x-small is-hot-note has-dropshadow"
                ><font-icon class="hot-note"></font-icon>
                {{ I18n.t("hot_note.received") }}</span
              >
            </span>
            <span
              v-if="approxOnlineStatus"
              class="inline-flex items-center gap-1 py-0.5"
            >
              <span class="flex justify-center items-center h-3 w-3 p-1">
                <span class="h-2 w-2 bg-online rounded-full"></span>
              </span>
              <span class="text-small frozen:text-brand-lightest">{{
                approxOnlineStatus
              }}</span>
            </span>
            <span
              v-if="props.user.isAvailable"
              class="inline-flex items-center gap-1 py-0.5 text-small frozen:text-brand-lightest"
            >
              <font-icon class="speed_sex--fill"></font-icon>
              <span>{{ I18n.t("user.available_tonight.name") }}</span>
            </span>

            <span class="block">
              <em class="title-big frozen:text-brand-lightest">{{
                user.username
              }}</em>
              <span class="text-super-big frozen:text-opacity-light-70 ml-1">{{
                compactAge
              }}</span>
            </span>

            <span class="flex gap-1">
              <span class="tag is-on-picture is-light is-x-small capitalize"
                ><UserType :size="12" :type="user.userTypeId" />
                {{ I18n.t("common.user_types.long")[user.userTypeId] }}</span
              >
              <span
                v-if="user.isCertified"
                class="tag is-on-picture is-light is-x-small"
                ><font-icon class="certification--fill"></font-icon>
                {{ tCertified }}</span
              >
              <span
                v-if="approxDistance"
                class="tag is-on-picture is-light is-x-small"
                ><font-icon class="geolocation--fill"></font-icon>
                {{ approxDistance }}</span
              >
            </span>

            <span class="grid gap-0.5 text-small frozen:text-brand-lightest">
              <span class="truncate">{{ openToMessage }}</span>
              <span v-if="commonFantasies.length">
                <strong>{{ I18n.t("common.in_common") }} : </strong
                >{{ commonFantasies.join(", ") }}
              </span>
            </span>
          </div>
        </a>

        <div class="user-card__actions">
          <form
            v-if="hotNoteVisible"
            :action="accountKisses.newHotNote.path({ id: user.id })"
            data-turbo-stream
            method="get"
          >
            <button
              :disabled="premiumKissDisabled"
              class="icon-button is-hot-note has-drop-shadow"
              type="submit"
            >
              <font-icon class="hot-note"></font-icon>
            </button>
          </form>
          <form
            :action="accountKisses.frenchKissUser.path({ id: user.id })"
            method="post"
          >
            <button
              :disabled="premiumKissDisabled"
              class="icon-button is-french-kiss has-drop-shadow"
              type="submit"
            >
              <font-icon class="french-kiss"></font-icon>
            </button>
          </form>
          <form :action="accountKisses.create.path({ id: user.id })" method="post">
            <button
              :disabled="kissDisabled"
              class="icon-button has-drop-shadow"
              type="submit"
            >
              <font-icon class="kiss"></font-icon>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
